// Das Licht aus dem Homelab-Protokoll (Topics `licht.bad`, `licht.ambient`,
// `licht.szene`; docs/protokoll.md) in genau die Form, die die Seiten aus den
// REST-Antworten GET /v2-api/bathroom, /ambient und /ambient/scenes kennen
// (`BathroomView`, `AmbientView`, `SceneView` aus ambient-summary.svelte.ts).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/licht-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert an
// (`skaliere("licht.bad", …)`: Sonnenzeiten und Phasenstart in Stunden);
// Gegenstück der Rechnung ist internal/protokoll/adapter/licht.go.
//
// Regel, mit der die REST-Antwort exakt wiederkehrt (der Adapter sendet
// spiegelbildlich, wie beim Speicher):
//   - Felder, die die REST-Antwort ohne `omitempty` führt, stehen immer:
//     fehlt der Wert im Stand, wird er ""/0/false, eine Liste []. (Der Adapter
//     lässt leere Texte und leere Listen weg, Zahlen und Wahrheitswerte ohne
//     omitempty sendet er immer.)
//   - Felder mit `omitempty` stehen am Ergebnis nur, wenn der Stand sie trägt.
//   - Die Phasen sind im Stand eine Liste mit `schluessel` (morning, day, …);
//     die REST-Antwort führt sie als Objekt nach diesem Schlüssel.
//   - `start` einer Phase ist in der REST-Antwort immer ein Objekt, ggf. `{}`.
//   - Die Ziele einer Szene tragen ihre Nutzlast als JSON-Text.

import type {
  AdaptiveCurvePt,
  AdaptivePoint,
  AdaptiveSegment,
  AdaptiveView,
  AmbientPhaseView,
  AmbientSun,
  AmbientTarget,
  AmbientView,
  BathroomPhaseView,
  BathroomTriggers,
  BathroomView,
  PhaseBoundary,
  Preset,
  SceneTarget,
  SceneView,
} from "./ambient-summary.svelte";
import type {
  AmbientPhase,
  BadPhase,
  LernAbschnitt,
  LernKurvenPunkt,
  LernPunkt,
  LichtAmbient,
  LichtBad,
  LichtGrenze,
  LichtSzene,
  LichtVoreinstellung,
  LichtZiel,
  SzenenZiel,
} from "./protokoll/schema.gen";

export type LichtArt = "bad" | "ambient" | "szene";

/** Instanzname → Art und Id: „licht.bad.ab12…“ → { art: "bad", id }. Alles
 *  andere → undefined. */
export function lichtArtAusInstanz(instanz: string): { art: LichtArt; id: string } | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "licht" || t[2] === "") return undefined;
  if (t[1] === "bad" || t[1] === "ambient" || t[1] === "szene") return { art: t[1], id: t[2] };
  return undefined;
}

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";
const n = (v: number | null | undefined): number => v ?? 0;
const b = (v: boolean | null | undefined): boolean => v ?? false;

/** Setzt `o[k]`, wenn der Stand einen Wert trägt (auch 0/false/""). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v != null) o[k] = v as T[K];
}

/** Die Grenze einer Phase; leer → `{}` (die REST-Antwort führt `start` immer). */
function grenze(g: LichtGrenze | null | undefined): PhaseBoundary {
  const o: PhaseBoundary = {};
  if (g == null) return o;
  if (g.sonne != null) o.sun = g.sonne as PhaseBoundary["sun"];
  wenn(o, "offset_min", g.versatz_min);
  wenn(o, "time", g.uhrzeit);
  return o;
}

function punkt(p: LernPunkt): AdaptivePoint {
  const o: AdaptivePoint = { minute: n(p.minute), brightness: n(p.helligkeit) };
  wenn(o, "confirm", p.bestaetigt);
  return o;
}

function kurvenPunkt(p: LernKurvenPunkt): AdaptiveCurvePt {
  const o: AdaptiveCurvePt = { minute: n(p.minute), brightness: n(p.helligkeit), confident: b(p.sicher) };
  wenn(o, "filled", p.gefuellt);
  wenn(o, "prov_brightness", p.vorlaeufig);
  return o;
}

function abschnitt(a: LernAbschnitt): AdaptiveSegment {
  return { start_min: n(a.von_min), end_min: n(a.bis_min), brightness: n(a.helligkeit) };
}

/** Die Felder der lernenden Helligkeit, die Bad und Ambient gemeinsam tragen. */
type LernFelder = Pick<
  LichtBad,
  | "lernen"
  | "lernen_folgen"
  | "lern_anzahl"
  | "lern_proben"
  | "lern_kurve"
  | "lern_abschnitte"
  | "jetzt_helligkeit"
  | "jetzt_sicher"
  | "jetzt_vorlaeufig"
  | "wunsch"
>;

function adaptiv(l: LernFelder): AdaptiveView {
  const o: AdaptiveView = {
    enabled: b(l.lernen),
    track: b(l.lernen_folgen),
    sample_count: n(l.lern_anzahl),
    samples: (l.lern_proben ?? []).map(punkt),
    curve: (l.lern_kurve ?? []).map(kurvenPunkt),
    segments: (l.lern_abschnitte ?? []).map(abschnitt),
    now_brightness: n(l.jetzt_helligkeit),
    now_confident: b(l.jetzt_sicher),
  };
  wenn(o, "now_prov_brightness", l.jetzt_vorlaeufig);
  if (l.wunsch != null) o.pending = punkt(l.wunsch);
  return o;
}

/** Sonnenzeiten und Vorhersage der nächsten Phase. */
function sonne(
  l: Pick<LichtBad, "sonnenaufgang" | "sonnenuntergang" | "naechste_phase" | "naechste_phase_um">,
): AmbientSun {
  const o: AmbientSun = { sunrise_h: n(l.sonnenaufgang), sunset_h: n(l.sonnenuntergang) };
  wenn(o, "next_phase", l.naechste_phase);
  wenn(o, "next_phase_at_unix", l.naechste_phase_um);
  return o;
}

/** Die Phasen-Liste des Stands als Objekt nach `schluessel`. Doppelte
 *  Schlüssel: der erste gilt (der Adapter sendet keine). */
function phasenObjekt<P extends { schluessel?: string | null }, V>(liste: readonly P[] | null | undefined, aus: (p: P) => V): Record<string, V> {
  const o: Record<string, V> = {};
  for (const p of liste ?? []) {
    const k = s(p.schluessel);
    if (!(k in o)) o[k] = aus(p);
  }
  return o;
}

function badPhase(p: BadPhase): BathroomPhaseView {
  const o: BathroomPhaseView = {
    label: s(p.label),
    brightness: n(p.helligkeit),
    default_brightness: n(p.standard_helligkeit),
    brightness_override: b(p.helligkeit_eigen),
    color_temp: n(p.farbtemp),
    default_color_temp: n(p.standard_farbtemp),
    color_temp_override: b(p.farbtemp_eigen),
    start: grenze(p.start),
    start_hour: n(p.start_stunde),
  };
  wenn(o, "transition", p.uebergang);
  return o;
}

function voreinstellung(v: LichtVoreinstellung): Preset {
  return { name: s(v.name), colors: [...(v.farben ?? [])] };
}

function ambientPhase(p: AmbientPhase): AmbientPhaseView {
  return {
    label: s(p.label),
    presets: (p.voreinstellungen ?? []).map(voreinstellung),
    active_preset: n(p.gewaehlt),
    brightness: n(p.helligkeit),
    default_brightness: n(p.standard_helligkeit),
    override: b(p.helligkeit_eigen),
    start: grenze(p.start),
    start_hour: n(p.start_stunde),
  };
}

function ziel(z: LichtZiel): AmbientTarget {
  const o: AmbientTarget = { topic: s(z.topic) };
  wenn(o, "type", z.typ);
  return o;
}

/** Die Bewegungslicht-Regel aus ihrem Stand, wie `GET /v2-api/bathroom` sie führt. */
export function badAusProtokoll(l: LichtBad): BathroomView {
  const ausloeser: BathroomTriggers = { motion_timeout: n(l.bewegung_frist) };
  wenn(ausloeser, "motion_topic", l.bewegung_topic);
  wenn(ausloeser, "door_topic", l.tuer_topic);
  wenn(ausloeser, "dimmer_topic", l.dimmer_topic);
  if (l.schalter_topics != null) ausloeser.switch_topics = [...l.schalter_topics];
  wenn(ausloeser, "door_off_delay", l.tuer_verzug);
  const o: BathroomView = {
    name: s(l.name),
    enabled: b(l.aktiv),
    on: b(l.an),
    manual_override: b(l.manuell),
    light_topic: s(l.lampe),
    current_phase: s(l.phase),
    phase_label: s(l.phase_label),
    phases: phasenObjekt(l.phasen, badPhase),
    sun: sonne(l),
    triggers: ausloeser,
    evening_end: n(l.abend_ende),
    adaptive: adaptiv(l),
  };
  wenn(o, "last_source", l.quelle);
  return o;
}

/** Die Ambient-Regel aus ihrem Stand, wie `GET /v2-api/ambient` sie führt. */
export function ambientAusProtokoll(l: LichtAmbient): AmbientView {
  const o: AmbientView = {
    name: s(l.name),
    enabled: b(l.aktiv),
    on: b(l.an),
    current_phase: s(l.phase),
    phase_label: s(l.phase_label),
    phases: phasenObjekt(l.phasen, ambientPhase),
    sun: sonne(l),
    targets: (l.ziele ?? []).map(ziel),
    phase_transition_s: n(l.uebergang),
    default_phase_transition_s: n(l.uebergang_standard),
    max_phase_transition_s: n(l.uebergang_max),
    evening_end: n(l.abend_ende),
    adaptive: adaptiv(l),
  };
  wenn(o, "active_scene", l.szene);
  return o;
}

/** Nutzlast eines Szenen-Ziels: der JSON-Text des Stands; leer oder kaputt → `{}`. */
function nutzlast(text: string | null | undefined): Record<string, unknown> {
  if (!text) return {};
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function szenenZiel(z: SzenenZiel): SceneTarget {
  const o: SceneTarget = { topic: s(z.topic), payload: nutzlast(z.payload) };
  wenn(o, "type", z.typ);
  return o;
}

/** Die Szene aus ihrem Stand, wie `GET /v2-api/ambient/scenes` sie führt. */
export function szeneAusProtokoll(l: LichtSzene): SceneView {
  const o: SceneView = {
    name: s(l.name),
    enabled: b(l.aktiv),
    active: b(l.an),
    on_go_engine: b(l.go_engine),
    target_count: n(l.ziel_anzahl),
    targets: (l.ziele ?? []).map(szenenZiel),
  };
  wenn(o, "restore_ambient", l.ambient_zurueck);
  if (l.farben != null) o.colors = [...l.farben];
  return o;
}
